import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import { googleMapsService } from '../services/googleMaps.service';

const mapContainerStyle = {
  width: '100%',
  height: '80vh',
  minHeight: 400,
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const iframeMapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: 300,
  borderRadius: 4,
  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
};

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString();
}

/** Min driver movement (meters) before recalculating Directions. */
const ROUTE_RECALC_DISTANCE_M = 75;
/** Min interval (ms) between Directions recalculations while tracking. */
const ROUTE_RECALC_INTERVAL_MS = 30_000;

export default function VendorLiveTracking({ serviceId, isIframe = false, polyline = null }) {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const googleRef = useRef(null);
  const isFirstLocationRef = useRef(true);
  const lastEndpointRef = useRef(null);
  const lastRouteOriginRef = useRef(null);
  const lastRouteCalcAtRef = useRef(0);
  const polylineOverlayRef = useRef(null);
  const lastDrawnPolylineRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationStatus, setLocationStatus] = useState('waiting');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [location, setLocation] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [polylineData, setPolylineData] = useState(null);

  const numericServiceId = serviceId ? Number(serviceId) : null;
  const convexLocation = useQuery(
    api.services.getServiceLocation,
    numericServiceId ? { serviceId: numericServiceId } : 'skip',
  );
  const convexPolyline = useQuery(
    api.services.getServicePolyline,
    numericServiceId && !polyline ? { serviceId: numericServiceId } : 'skip',
  );

  const resolvedPolyline =
    polyline ||
    convexPolyline?.polylineEncoded ||
    convexPolyline?.polyline ||
    null;

  const runningDistanceKm =
    typeof convexPolyline?.runningDistanceKm === 'number'
      ? convexPolyline.runningDistanceKm
      : typeof convexPolyline?.runningDistance === 'number'
        ? convexPolyline.runningDistance / 1000
        : null;

  const triggerMapResize = useCallback((map, google) => {
    if (!map || !google?.maps?.event) return;
    requestAnimationFrame(() => {
      google.maps.event.trigger(map, 'resize');
    });
    setTimeout(() => {
      google.maps.event.trigger(map, 'resize');
    }, 250);
  }, []);

  // Initialize Google Map once.
  useEffect(() => {
    let cancelled = false;

    async function setupMap() {
      try {
        if (!mapRef.current) return;

        const apiKey = await googleMapsService.getApiKey();
        const loader = new Loader({
          apiKey,
          version: 'weekly',
          libraries: ['geometry'],
        });
        const google = await loader.load();
        if (cancelled) return;

        googleRef.current = google;

        const map = new google.maps.Map(mapRef.current, {
          center: { lat: 20.5937, lng: 78.9629 },
          zoom: 14,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: true,
          rotateControl: true,
          fullscreenControl: true,
          gestureHandling: 'cooperative',
          maxZoom: 20,
          minZoom: 1,
        });

        map.addListener('click', () => map.set('userInteraction', true));
        map.addListener('zoom_changed', () => map.set('userInteraction', true));
        map.addListener('center_changed', () => map.set('userInteraction', true));
        map.addListener('dragstart', () => map.set('userInteraction', true));
        map.addListener('dragend', () => map.set('userInteraction', true));

        markerRef.current = new google.maps.Marker({
          map,
          title: 'Driver Location',
          icon: {
            url: '/img/service_car_icon.png',
            scaledSize: new google.maps.Size(40, 40),
            anchor: new google.maps.Point(20, 40),
          },
          animation: google.maps.Animation.DROP,
        });

        pickupMarkerRef.current = new google.maps.Marker({
          map,
          title: 'Pickup Location',
          icon: {
            url:
              'data:image/svg+xml;charset=UTF-8,' +
              encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 2C10.48 2 6 6.48 6 12c0 7.5 10 18 10 18s10-10.5 10-18c0-5.52-4.48-10-10-10zm0 14c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" fill="#EA4335"/>
                <circle cx="16" cy="12" r="3" fill="#FFFFFF"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 32),
          },
          visible: false,
        });

        infoWindowRef.current = new google.maps.InfoWindow({
          content: `<div><strong>Service ID:</strong> ${serviceId}<br/><span id="last-updated">Last updated: --</span></div>`,
        });

        markerRef.current.addListener('click', () => {
          infoWindowRef.current.open(map, markerRef.current);
        });

        directionsRendererRef.current = new google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: '#2196F3',
            strokeWeight: 4,
            strokeOpacity: 0.8,
          },
          preserveViewport: true,
          hideRouteList: true,
          suppressInfoWindows: true,
          autoRoute: false,
          draggable: false,
        });

        setMapInstance(map);
        triggerMapResize(map, google);
        setLoading(false);
      } catch (err) {
        console.error('📍 [WEB] Map setup error:', err);
        setError(`Failed to load map: ${err.message}`);
        setLoading(false);
        setLocationStatus('error');
      }
    }

    setupMap();

    return () => {
      cancelled = true;
      if (polylineOverlayRef.current) {
        polylineOverlayRef.current.setMap(null);
        polylineOverlayRef.current = null;
      }
      lastDrawnPolylineRef.current = null;
      lastEndpointRef.current = null;
      lastRouteOriginRef.current = null;
      lastRouteCalcAtRef.current = 0;
      isFirstLocationRef.current = true;
      setPolylineData(null);
      setRouteInfo(null);
      setLocation(null);
      setLastUpdate(null);
      setLocationStatus('waiting');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  // Draw / refresh orange breadcrumb whenever Convex (or prop) polyline changes.
  useEffect(() => {
    if (!mapInstance || !googleRef.current || !resolvedPolyline) return;

    if (lastDrawnPolylineRef.current === resolvedPolyline) {
      setPolylineData((prev) =>
        prev && prev.runningDistance !== runningDistanceKm
          ? { ...prev, runningDistance: runningDistanceKm }
          : prev,
      );
      return;
    }

    displayPolylineRoute(
      googleRef.current,
      mapInstance,
      resolvedPolyline,
      polylineOverlayRef,
      setPolylineData,
      runningDistanceKm,
    );
    lastDrawnPolylineRef.current = resolvedPolyline;
  }, [mapInstance, resolvedPolyline, runningDistanceKm]);

  // Apply Convex location updates to the map.
  useEffect(() => {
    if (!mapInstance || !googleRef.current || convexLocation === undefined) return;

    const google = googleRef.current;
    const map = mapInstance;

    if (!convexLocation) {
      setLocationStatus('no_data');
      return;
    }

    const loc = convexLocation;
    const isCompleted =
      loc.service_status === 'service_complete' || loc.service_status === 'service_resolved';

    if (loc.current_lat == null || loc.current_lng == null) {
      // Completed jobs may still show the breadcrumb without a live pin.
      if (isCompleted && resolvedPolyline) {
        setLocationStatus('completed');
        return;
      }
      setLocationStatus('no_data');
      return;
    }

    const pos = { lat: loc.current_lat, lng: loc.current_lng };
    smoothMoveMarker(pos);
    setLocation(loc);
    setLastUpdate(loc.updated_time || Date.now());
    setLocationStatus(isCompleted ? 'completed' : 'tracking');

    infoWindowRef.current?.setContent(
      `<div><strong>Service ID:</strong> ${serviceId}<br/><span id="last-updated">Last updated: ${formatTime(loc.updated_time || Date.now())}</span></div>`,
    );

    if (loc.end_location_lat != null && loc.end_location_lng != null) {
      const currentEndpoint = {
        lat: loc.end_location_lat,
        lng: loc.end_location_lng,
        name: loc.endpoint_name,
      };

      if (pickupMarkerRef.current) {
        pickupMarkerRef.current.setPosition({
          lat: loc.end_location_lat,
          lng: loc.end_location_lng,
        });
        pickupMarkerRef.current.setVisible(true);
        pickupMarkerRef.current.setTitle(loc.endpoint_name || 'Pickup Location');
      }

      const endpointChanged =
        !lastEndpointRef.current ||
        lastEndpointRef.current.lat !== currentEndpoint.lat ||
        lastEndpointRef.current.lng !== currentEndpoint.lng ||
        lastEndpointRef.current.name !== currentEndpoint.name;

      const now = Date.now();
      const movedMeters = lastRouteOriginRef.current
        ? calculateDistance(
            lastRouteOriginRef.current.lat,
            lastRouteOriginRef.current.lng,
            pos.lat,
            pos.lng,
          )
        : Infinity;
      const originMovedEnough = movedMeters >= ROUTE_RECALC_DISTANCE_M;
      const intervalElapsed = now - lastRouteCalcAtRef.current >= ROUTE_RECALC_INTERVAL_MS;
      const movedFar = movedMeters >= ROUTE_RECALC_DISTANCE_M * 3;
      const shouldRecalcRoute =
        endpointChanged ||
        !lastRouteOriginRef.current ||
        (originMovedEnough && intervalElapsed) ||
        movedFar;

      if (shouldRecalcRoute) {
        lastEndpointRef.current = currentEndpoint;
        lastRouteOriginRef.current = pos;
        lastRouteCalcAtRef.current = now;
        calculateRoute(
          google,
          pos,
          { lat: loc.end_location_lat, lng: loc.end_location_lng },
          loc.endpoint_name || 'Destination',
          directionsRendererRef,
          setRouteInfo,
        );
      }
    } else if (pickupMarkerRef.current) {
      pickupMarkerRef.current.setVisible(false);
      if (lastEndpointRef.current) {
        directionsRendererRef.current?.setDirections({ routes: [] });
        setRouteInfo(null);
        lastEndpointRef.current = null;
        lastRouteOriginRef.current = null;
        lastRouteCalcAtRef.current = 0;
      }
    }

    if (isFirstLocationRef.current && !map.get('userInteraction')) {
      if (loc.end_location_lat != null && loc.end_location_lng != null) {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(pos);
        bounds.extend({ lat: loc.end_location_lat, lng: loc.end_location_lng });
        map.fitBounds(bounds);
        const listener = google.maps.event.addListener(map, 'idle', () => {
          if (map.getZoom() > 15) map.setZoom(15);
          google.maps.event.removeListener(listener);
        });
      } else {
        map.setCenter(pos);
        map.setZoom(14);
      }
      isFirstLocationRef.current = false;
      triggerMapResize(map, google);
    } else if (isFirstLocationRef.current) {
      isFirstLocationRef.current = false;
    }
  }, [convexLocation, mapInstance, resolvedPolyline, serviceId, triggerMapResize]);

  const smoothMoveMarker = (newPosition) => {
    if (!markerRef.current) return;

    const currentPosition = markerRef.current.getPosition();
    if (!currentPosition) {
      markerRef.current.setPosition(newPosition);
      return;
    }

    const distance = calculateDistance(
      currentPosition.lat(),
      currentPosition.lng(),
      newPosition.lat,
      newPosition.lng,
    );

    if (distance < 1) {
      markerRef.current.setPosition(newPosition);
      return;
    }

    const steps = Math.min(Math.max(Math.floor(distance / 10), 10), 50);
    const stepDuration = 100;
    let step = 0;
    const startLat = currentPosition.lat();
    const startLng = currentPosition.lng();
    const latDiff = newPosition.lat - startLat;
    const lngDiff = newPosition.lng - startLng;

    const animate = () => {
      step += 1;
      const progress = step / steps;
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      markerRef.current.setPosition({
        lat: startLat + latDiff * easedProgress,
        lng: startLng + lngDiff * easedProgress,
      });
      if (step < steps) {
        setTimeout(animate, stepDuration);
      } else {
        markerRef.current.setPosition(newPosition);
      }
    };

    animate();
  };

  const containerStyle = isIframe ? { padding: '10px', height: '100%', display: 'flex', flexDirection: 'column' } : { padding: 24 };
  const mapStyle = isIframe ? iframeMapContainerStyle : mapContainerStyle;

  return (
    <div style={containerStyle}>
      {!isIframe && <h2>Vendor Live Tracking</h2>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {locationStatus === 'waiting' && !loading && (
        <div style={{ marginBottom: 12, padding: 10, backgroundColor: '#fff8e1', borderRadius: 4, border: '1px solid #ffc107' }}>
          Waiting for driver location updates…
        </div>
      )}
      {locationStatus === 'no_data' && !loading && (
        <div style={{ marginBottom: 12, padding: 10, backgroundColor: '#f5f5f5', borderRadius: 4, border: '1px solid #ccc' }}>
          No live location available yet for this service. Confirm the driver app is sharing location and Convex is reachable ({process.env.REACT_APP_CONVEX_URL || 'REACT_APP_CONVEX_URL'}).
        </div>
      )}

      {routeInfo && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#e3f2fd', borderRadius: 4, border: '1px solid #2196F3' }}>
          <strong>🚗 Route to {routeInfo.destinationName}:</strong>
          <br />
          Distance: {routeInfo.distance}
          <br />
          Duration: {routeInfo.duration}
          <br />
          From: {routeInfo.startAddress}
          <br />
          To: {routeInfo.endAddress}
        </div>
      )}

      {polylineData && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff3e0', borderRadius: 4, border: '1px solid #FF6B35' }}>
          <strong>📍 Actual Route Taken:</strong>
          <br />
          Coordinates: {polylineData.coordinateCount || 'N/A'}
          <br />
          {polylineData.runningDistance ? (
            <>
              Distance: {polylineData.runningDistance.toFixed(2)} km
              <br />
            </>
          ) : null}
          <em>Orange line shows the actual path taken by the driver</em>
        </div>
      )}

      <div style={{ position: 'relative', flex: isIframe ? 1 : undefined, minHeight: isIframe ? 300 : undefined }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: 4,
            }}
          >
            <div>Loading map…</div>
          </div>
        )}
        <div ref={mapRef} style={mapStyle} />
      </div>

      {lastUpdate && (locationStatus === 'tracking' || locationStatus === 'completed') && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
          Last update: {formatTime(lastUpdate)}
          {location?.driverName || location?.driver_name
            ? ` · Driver: ${location.driverName || location.driver_name}`
            : ''}
        </div>
      )}
    </div>
  );
}

function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

async function calculateRoute(google, origin, destination, destinationName, directionsRendererRef, setRouteInfo) {
  try {
    const directionsService = new google.maps.DirectionsService();
    // Promise API resolves to DirectionsResult (no .status); failures reject.
    const result = await directionsService.route({
      origin,
      destination,
      travelMode: google.maps.TravelMode.DRIVING,
      unitSystem: google.maps.UnitSystem.METRIC,
    });

    if (!result?.routes?.[0]?.legs?.[0] || !directionsRendererRef.current) {
      setRouteInfo(null);
      return;
    }

    directionsRendererRef.current.setDirections(result);
    directionsRendererRef.current.setOptions({ preserveViewport: true });
    const leg = result.routes[0].legs[0];
    setRouteInfo({
      distance: leg.distance?.text || '',
      duration: leg.duration?.text || '',
      startAddress: leg.start_address || '',
      endAddress: leg.end_address || '',
      destinationName,
    });
  } catch (err) {
    console.error('📍 [WEB] Directions request failed:', err);
    setRouteInfo(null);
  }
}

async function displayPolylineRoute(
  google,
  map,
  polylineString,
  polylineOverlayRef,
  setPolylineData,
  runningDistanceKm = null,
) {
  try {
    if (!google.maps.geometry) {
      throw new Error('Google Maps geometry library not loaded');
    }

    const decodedPath = google.maps.geometry.encoding.decodePath(polylineString);

    if (polylineOverlayRef.current) {
      polylineOverlayRef.current.setPath(decodedPath);
    } else {
      polylineOverlayRef.current = new google.maps.Polyline({
        path: decodedPath,
        geodesic: true,
        strokeColor: '#FF6B35',
        strokeOpacity: 1.0,
        strokeWeight: 4,
        map,
      });
    }

    setPolylineData({
      polylineEncoded: polylineString,
      coordinateCount: decodedPath.length,
      runningDistance: runningDistanceKm,
    });

    if (!map.get('userInteraction')) {
      const bounds = new google.maps.LatLngBounds();
      decodedPath.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds);
      const listener = google.maps.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 16) map.setZoom(16);
        google.maps.event.removeListener(listener);
      });
    }
  } catch (err) {
    console.error('📍 [WEB] Error displaying polyline route:', err);
  }
}
